import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Activity,
  Terminal,
  History,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Database,
  Cpu,
  Layers
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchAuditHealth,
  fetchAuditRuns,
  fetchAuditLogs,
  type AuditHealth,
  type MonitoringRun,
  type LogLine
} from "../../api/audit";

export default function AuditLogsPage() {
  const { theme } = useOutletContext<{ theme: "dark" | "light" }>();
  const isDark = theme === "dark";

  // Data States
  const [health, setHealth] = useState<AuditHealth | null>(null);
  const [runsData, setRunsData] = useState<{
    items: MonitoringRun[];
    total: number;
    page: number;
    pages: number;
  } | null>(null);
  const [logs, setLogs] = useState<LogLine[]>([]);

  // Loading States
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Pagination & Filter States for Runs History
  const [runsPage, setRunsPage] = useState(1);
  const [runsLimit, setRunsLimit] = useState(20);
  const [runsForumFilter, setRunsForumFilter] = useState("");
  const [runsStatusFilter, setRunsStatusFilter] = useState("");
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  // Log Viewer Filter States
  const [logFile, setLogFile] = useState<"app" | "monitor-error" | "worker-error">("app");
  const [logLinesLimit, setLogLinesLimit] = useState(200);
  const [logLevelFilter, setLogLevelFilter] = useState("");
  const [logSearchQuery, setLogSearchQuery] = useState("");

  // Load Health & Tracked Forums Data
  const loadHealth = async (silent = false) => {
    try {
      if (!silent) setLoadingHealth(true);
      const data = await fetchAuditHealth();
      setHealth(data);
    } catch (err: any) {
      toast.error("Failed to load scraper system health: " + (err.message || err));
    } finally {
      setLoadingHealth(false);
    }
  };

  // Load Runs History
  const loadRuns = async () => {
    try {
      setLoadingRuns(true);
      const data = await fetchAuditRuns({
        page: runsPage,
        limit: runsLimit,
        forum: runsForumFilter || undefined,
        status: runsStatusFilter || undefined,
      });
      setRunsData(data);
    } catch (err: any) {
      toast.error("Failed to load runs history: " + (err.message || err));
    } finally {
      setLoadingRuns(false);
    }
  };

  // Load Logs
  const loadLogs = async () => {
    try {
      setLoadingLogs(true);
      const data = await fetchAuditLogs({
        file: logFile,
        lines: logLinesLimit,
        level: logLevelFilter || undefined,
        search: logSearchQuery || undefined,
      });
      setLogs(data);
      toast.success(`Successfully loaded ${data.length} log lines`);
    } catch (err: any) {
      toast.error("Failed to retrieve log lines: " + (err.message || err));
    } finally {
      setLoadingLogs(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadHealth();
    loadLogs();
  }, []);

  // Sync runs when page, filters, or limit change
  useEffect(() => {
    loadRuns();
  }, [runsPage, runsLimit, runsStatusFilter]);

  const handleRunsSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setRunsPage(1);
    loadRuns();
  };

  const handleLogsSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadLogs();
  };

  const toggleExpandRun = (id: string) => {
    setExpandedRunId(expandedRunId === id ? null : id);
  };

  // Theme variable map
  const themeVars = isDark ? {
    bg: "#080a14",
    cardBg: "#101222",
    cardBorder: "#1a1c32",
    textMain: "#ffffff",
    textMuted: "rgba(255, 255, 255, 0.4)",
    textSubtle: "rgba(255, 255, 255, 0.6)",
    innerBorder: "rgba(255, 255, 255, 0.03)",
    inputBg: "rgba(255, 255, 255, 0.04)",
    inputBorder: "rgba(255, 255, 255, 0.08)",
    rowHoverBg: "rgba(255, 255, 255, 0.02)",
    tableRowBorder: "rgba(255, 255, 255, 0.03)",
    statCardBg: "rgba(255, 255, 255, 0.02)",
    statCardBorder: "rgba(255, 255, 255, 0.05)",
    svcCardBg: "rgba(255, 255, 255, 0.03)",
    svcCardBorder: "rgba(255, 255, 255, 0.06)",
    terminalBg: "#030406",
    terminalBorder: "rgba(255, 255, 255, 0.1)",
    terminalText: "#e5e7eb",
    cardShadow: "0 15px 35px rgba(0, 0, 0, 0.5)"
  } : {
    bg: "#f4f6fa",
    cardBg: "#ffffff",
    cardBorder: "#cbd5e1",
    textMain: "#0f172a",
    textMuted: "#64748b",
    textSubtle: "#334155",
    innerBorder: "#e2e8f0",
    inputBg: "#ffffff",
    inputBorder: "#cbd5e1",
    rowHoverBg: "rgba(0, 0, 0, 0.03)",
    tableRowBorder: "#e2e8f0",
    statCardBg: "#f8fafc",
    statCardBorder: "#e2e8f0",
    svcCardBg: "#f8fafc",
    svcCardBorder: "#e2e8f0",
    terminalBg: "#f8fafc",
    terminalBorder: "#cbd5e1",
    terminalText: "#0f172a",
    cardShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
  };

  return (
    <div className="aud-page animate-enter">
      <style dangerouslySetInnerHTML={{__html: `
        :root {
          --aud-bg: ${themeVars.bg};
          --aud-card-bg: ${themeVars.cardBg};
          --aud-card-border: ${themeVars.cardBorder};
          --aud-text-main: ${themeVars.textMain};
          --aud-text-muted: ${themeVars.textMuted};
          --aud-text-subtle: ${themeVars.textSubtle};
          --aud-inner-border: ${themeVars.innerBorder};
          --aud-input-bg: ${themeVars.inputBg};
          --aud-input-border: ${themeVars.inputBorder};
          --aud-row-hover-bg: ${themeVars.rowHoverBg};
          --aud-table-row-border: ${themeVars.tableRowBorder};
          --aud-stat-card-bg: ${themeVars.statCardBg};
          --aud-stat-card-border: ${themeVars.statCardBorder};
          --aud-svc-card-bg: ${themeVars.svcCardBg};
          --aud-svc-card-border: ${themeVars.svcCardBorder};
          --aud-terminal-bg: ${themeVars.terminalBg};
          --aud-terminal-border: ${themeVars.terminalBorder};
          --aud-terminal-text: ${themeVars.terminalText};
          --aud-card-shadow: ${themeVars.cardShadow};
        }

        .aud-page {
          background-color: var(--aud-bg) !important;
          color: var(--aud-text-main) !important;
        }

        .aud-input-control {
          background: var(--aud-input-bg);
          border: 1px solid var(--aud-input-border);
          color: var(--aud-text-main);
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 13px;
          outline: none;
          transition: border-color 0.15s ease;
        }

        .aud-input-control:focus {
          border-color: rgba(0, 102, 224, 0.6);
        }

        .aud-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: inherit;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }

        .aud-btn-primary {
          background: linear-gradient(135deg, #818cf8 0%, #6366F1 100%);
          color: #fff;
          box-shadow: 0 4px 12px rgba(0, 102, 224, 0.25);
        }

        .aud-btn-primary:hover {
          transform: translateY(-1px);
          opacity: 0.95;
        }

        .aud-btn-secondary {
          background: var(--aud-input-bg);
          border: 1px solid var(--aud-input-border);
          color: var(--aud-text-main);
        }

        .aud-btn-secondary:hover {
          background: var(--aud-row-hover-bg);
        }

        .aud-pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid var(--aud-input-border);
          background: var(--aud-input-bg);
          color: var(--aud-text-main);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .aud-pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .aud-pagination-btn:hover:not(:disabled) {
          background: var(--aud-row-hover-bg);
        }
      `}} />

      {/* ── Page Header ── */}
      <div className="aud-header">
        <div>
          <h1 className="aud-title">Audit Logs</h1>
          <p className="aud-sub">Monitor background service health, review ingestion runs, and tail diagnostic log files.</p>
        </div>
        <button className="aud-btn aud-btn-secondary" onClick={() => { loadHealth(); loadRuns(); loadLogs(); }} id="btn-refresh-all">
          <RefreshCw size={14} />
          Refresh Data
        </button>
      </div>

      {/* ── Services & Aggregate Stats Grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 20 }}>
        {/* Scraper Services Status */}
        <div className="aud-card">
          <h2 style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
            <Cpu size={16} style={{ color: "#818cf8" }} />
            Background Scrapper System Services
          </h2>
          {loadingHealth ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--aud-text-muted)" }}>Loading services status...</div>
          ) : health ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="aud-svc-card">
                <span className="aud-svc-name">darknet-monitor (Scheduler)</span>
                <span className={`aud-status-pill aud-status-${health.services["darknet-monitor"]}`}>
                  {health.services["darknet-monitor"]}
                </span>
              </div>
              <div className="aud-svc-card">
                <span className="aud-svc-name">darknet-worker (Scraper Worker)</span>
                <span className={`aud-status-pill aud-status-${health.services["darknet-worker"]}`}>
                  {health.services["darknet-worker"]}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                <div style={{ background: "rgba(0,102,224,0.04)", border: "1px solid var(--aud-inner-border)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "var(--aud-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                    <Database size={12} />
                    Redis queue (jobs:tor)
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, fontFamily: "monospace" }}>
                    {health.queues.jobs_tor}
                  </div>
                </div>
                <div style={{ background: "rgba(244,63,94,0.04)", border: "1px solid var(--aud-inner-border)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "var(--aud-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                    <Database size={12} />
                    High Priority (jobs:tor:high)
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, fontFamily: "monospace" }}>
                    {health.queues.jobs_tor_high}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: "var(--aud-text-muted)" }}>No services data available.</div>
          )}
        </div>

        {/* Scraper Performance Stats */}
        <div className="aud-card">
          <h2 style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
            <Layers size={16} style={{ color: "#3b82f6" }} />
            Ingestion Performance Stats
          </h2>
          {loadingHealth ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--aud-text-muted)" }}>Loading statistics...</div>
          ) : health ? (
            <div className="aud-grid-stats">
              <div className="aud-stat-card">
                <span className="aud-stat-label">Total Forums Tracked</span>
                <span className="aud-stat-val">{health.stats.total_forums}</span>
                <span className="aud-stat-sub">Configured in scraper</span>
              </div>
              <div className="aud-stat-card">
                <span className="aud-stat-label">Succeeding Forums</span>
                <span className="aud-stat-val" style={{ color: "#10b981" }}>{health.stats.succeeding_forums}</span>
                <span className="aud-stat-sub">Last run succeeded</span>
              </div>
              <div className="aud-stat-card">
                <span className="aud-stat-label">Failing Forums</span>
                <span className="aud-stat-val" style={{ color: health.stats.failing_forums > 0 ? "#ef4444" : "var(--aud-text-main)" }}>
                  {health.stats.failing_forums}
                </span>
                <span className="aud-stat-sub">Last run failed</span>
              </div>
              <div className="aud-stat-card">
                <span className="aud-stat-label">Last Success Run</span>
                <span className="aud-stat-val" style={{ fontSize: 12, wordBreak: "break-all", height: 42, display: "flex", alignItems: "center" }}>
                  {health.stats.last_successful_run_at
                    ? new Date(health.stats.last_successful_run_at).toLocaleString()
                    : "No runs captured"}
                </span>
                <span className="aud-stat-sub">System-wide success</span>
              </div>
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: "var(--aud-text-muted)" }}>No stats data available.</div>
          )}
        </div>
      </div>

      {/* ── Tracked Forums Status Table ── */}
      <div className="aud-card">
        <h2 style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <Activity size={16} style={{ color: "#10b981" }} />
          Tracked Forums &amp; Last Run Health
        </h2>
        <div style={{ overflowX: "auto" }}>
          {loadingHealth ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--aud-text-muted)" }}>Loading forums...</div>
          ) : health && health.forums.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--aud-table-row-border)", color: "var(--aud-text-muted)" }}>
                  <th style={{ padding: "10px 12px" }}>Forum</th>
                  <th style={{ padding: "10px 12px" }}>Last Status</th>
                  <th style={{ padding: "10px 12px" }}>Last Checked</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>URLs Found</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {health.forums.map((forum, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid var(--aud-table-row-border)" }}>
                    <td style={{ padding: "12px", fontWeight: 600 }}>{forum.forum}</td>
                    <td style={{ padding: "12px" }}>
                      <span className={`aud-status-pill aud-status-${forum.status}`} style={{ fontSize: 9, padding: "2px 6px" }}>
                        {forum.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px", color: "var(--aud-text-subtle)" }}>
                      {forum.last_run_at ? new Date(forum.last_run_at).toLocaleString() : "Never run"}
                    </td>
                    <td style={{ padding: "12px", textAlign: "right", fontFamily: "monospace" }}>{forum.urls_found}</td>
                    <td style={{ padding: "12px", textAlign: "right", fontFamily: "monospace", color: forum.errors > 0 ? "#ef4444" : "inherit" }}>
                      {forum.errors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 25, textAlign: "center", color: "var(--aud-text-muted)" }}>No tracked forums found in DB.</div>
          )}
        </div>
      </div>

      {/* ── Expandable "Raw Run History" Table ── */}
      <div className="aud-card">
        <h2 style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <History size={16} style={{ color: "#f59e0b" }} />
          Raw Scraper Run History
        </h2>

        {/* Filter bar */}
        <form onSubmit={handleRunsSearch} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Forum Name</label>
            <input
              type="text"
              placeholder="e.g. breachforums"
              className="aud-input-control"
              value={runsForumFilter}
              onChange={(e) => setRunsForumFilter(e.target.value)}
              style={{ width: 180 }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Status</label>
            <select
              className="aud-input-control"
              value={runsStatusFilter}
              onChange={(e) => setRunsStatusFilter(e.target.value)}
              style={{ width: 140 }}
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Page Limit</label>
            <select
              className="aud-input-control"
              value={runsLimit}
              onChange={(e) => { setRunsLimit(Number(e.target.value)); setRunsPage(1); }}
              style={{ width: 100 }}
            >
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
          </div>
          <button type="submit" className="aud-btn aud-btn-primary" style={{ alignSelf: "flex-end", height: 34 }}>
            <Search size={14} />
            Search
          </button>
        </form>

        {/* History Table */}
        <div style={{ overflowX: "auto" }}>
          {loadingRuns ? (
            <div style={{ padding: 25, textAlign: "center", color: "var(--aud-text-muted)" }}>Loading run history...</div>
          ) : runsData && runsData.items.length > 0 ? (
            <>
              <table className="aud-run-table" style={{ fontSize: 13, textAlign: "left" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--aud-table-row-border)", color: "var(--aud-text-muted)" }}>
                    <th style={{ padding: "10px 12px", width: 24 }} />
                    <th style={{ padding: "10px 12px" }}>Run ID</th>
                    <th style={{ padding: "10px 12px" }}>Forum</th>
                    <th style={{ padding: "10px 12px" }}>Started At</th>
                    <th style={{ padding: "10px 12px" }}>Status</th>
                    <th style={{ padding: "10px 12px", textAlign: "right" }}>New URLs</th>
                    <th style={{ padding: "10px 12px", textAlign: "right" }}>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {runsData.items.map((run) => {
                    const isExpanded = expandedRunId === run.id;
                    return (
                      <optgroup key={run.id} style={{ border: "none" }}>
                        <tr className="aud-run-row" onClick={() => toggleExpandRun(run.id)}>
                          <td style={{ padding: "12px", color: "var(--aud-text-muted)" }}>
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </td>
                          <td style={{ padding: "12px", fontFamily: "monospace", fontSize: 12, color: "var(--aud-text-subtle)" }}>
                            #{run.id.substring(run.id.length - 8)}
                          </td>
                          <td style={{ padding: "12px", fontWeight: 600 }}>{run.forum}</td>
                          <td style={{ padding: "12px", color: "var(--aud-text-subtle)" }}>
                            {run.run_started_at ? new Date(run.run_started_at).toLocaleString() : "—"}
                          </td>
                          <td style={{ padding: "12px" }}>
                            <span className={`aud-status-pill aud-status-${run.status}`} style={{ fontSize: 9, padding: "2px 6px" }}>
                              {run.status}
                            </span>
                          </td>
                          <td style={{ padding: "12px", textAlign: "right", fontFamily: "monospace" }}>{run.new_urls}</td>
                          <td style={{ padding: "12px", textAlign: "right", fontFamily: "monospace", color: run.errors > 0 ? "#ef4444" : "inherit" }}>
                            {run.errors}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="aud-run-details">
                            <td colSpan={7} style={{ padding: 0 }}>
                              <div className="aud-details-content">
                                <div className="aud-detail-item">
                                  <span className="aud-detail-label">Full MongoDB ID</span>
                                  <span className="aud-detail-val">{run.id}</span>
                                </div>
                                <div className="aud-detail-item">
                                  <span className="aud-detail-label">Run Started At</span>
                                  <span className="aud-detail-val">{run.run_started_at ? new Date(run.run_started_at).toLocaleString() : "—"}</span>
                                </div>
                                <div className="aud-detail-item">
                                  <span className="aud-detail-label">Run Finished At</span>
                                  <span className="aud-detail-val">{run.run_finished_at ? new Date(run.run_finished_at).toLocaleString() : "—"}</span>
                                </div>
                                <div className="aud-detail-item">
                                  <span className="aud-detail-label">Total URLs Found</span>
                                  <span className="aud-detail-val">{run.urls_found}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </optgroup>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination controls */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                <span style={{ fontSize: 12, color: "var(--aud-text-muted)" }}>
                  Showing page {runsData.page} of {runsData.pages} (Total: {runsData.total} runs)
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="aud-pagination-btn"
                    disabled={runsPage === 1}
                    onClick={() => setRunsPage(runsPage - 1)}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="aud-pagination-btn"
                    disabled={runsPage >= runsData.pages}
                    onClick={() => setRunsPage(runsPage + 1)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: 25, textAlign: "center", color: "var(--aud-text-muted)" }}>No scraper runs match the filters.</div>
          )}
        </div>
      </div>

      {/* ── Diagnostic Log Viewer (Terminal Style) ── */}
      <div className="aud-card">
        <h2 style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <Terminal size={16} style={{ color: "#ef4444" }} />
          Diagnostic Log Viewer
        </h2>

        {/* Log Filters */}
        <form onSubmit={handleLogsSearch} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Log Source File</label>
            <select
              className="aud-input-control"
              value={logFile}
              onChange={(e) => setLogFile(e.target.value as any)}
              style={{ width: 180 }}
            >
              <option value="app">app.log (All Logs)</option>
              <option value="monitor-error">monitor-error.log</option>
              <option value="worker-error">worker-error.log</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Log Level</label>
            <select
              className="aud-input-control"
              value={logLevelFilter}
              onChange={(e) => setLogLevelFilter(e.target.value)}
              style={{ width: 120 }}
            >
              <option value="">All Levels</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
              <option value="DEBUG">DEBUG</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Lines Limit</label>
            <select
              className="aud-input-control"
              value={logLinesLimit}
              onChange={(e) => setLogLinesLimit(Number(e.target.value))}
              style={{ width: 120 }}
            >
              <option value="100">Last 100 lines</option>
              <option value="200">Last 200 lines</option>
              <option value="500">Last 500 lines</option>
              <option value="1000">Last 1000 lines</option>
              <option value="2000">Last 2000 lines</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 11, color: "var(--aud-text-muted)" }}>Filter Keyword</label>
            <input
              type="text"
              placeholder="Search in log contents..."
              className="aud-input-control"
              value={logSearchQuery}
              onChange={(e) => setLogSearchQuery(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <button type="submit" className="aud-btn aud-btn-primary" style={{ alignSelf: "flex-end", height: 34 }} disabled={loadingLogs}>
            {loadingLogs ? <RefreshCw className="animate-spin" size={14} /> : <Search size={14} />}
            Fetch Logs
          </button>
        </form>

        {/* Terminal Screen */}
        <div className="aud-terminal">
          {loadingLogs ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, height: 100 }}>
              <RefreshCw className="animate-spin" size={16} style={{ color: "#a855f7" }} />
              <span>Fetching logs from cloud server...</span>
            </div>
          ) : logs.length === 0 ? (
            <div style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>
              No log entries match the search constraints.
            </div>
          ) : (
            logs.map((log, idx) => {
              const lvl = log.level.toLowerCase();
              const levelClass =
                lvl === "info"
                  ? "aud-level-info"
                  : lvl === "warning" || lvl === "warn"
                  ? "aud-level-warn"
                  : lvl === "error" || lvl === "crit" || lvl === "critical"
                  ? "aud-level-error"
                  : lvl === "debug"
                  ? "aud-level-debug"
                  : "";

              return (
                <div key={idx} className="aud-log-item">
                  {log.timestamp && <span className="aud-log-time">{log.timestamp}</span>}
                  <span className={`aud-log-level ${levelClass}`}>[{log.level}]</span>
                  {log.logger && <span className="aud-log-logger">{log.logger}:</span>}
                  <span className="aud-log-msg">{log.message}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
